import React, { useCallback } from 'react';
import { Input, type InputRef } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { resolveDesignModeValue, type CompiledDesignToken } from 'common/design-tokens';
import t from 'utils/translate';
import { pickerSearchClassName } from 'admin/components/common/PickerSearch';
import css from './DesignTokenPicker.scss';

type TokenSource = {
	id: string;
	label?: string;
	tokens: CompiledDesignToken[];
	selectedTokenId?: string;
	/** Tokens left out of the list, such as Brand Kit tokens the Story already has; the heading counts them */
	hiddenTokenIds?: ReadonlySet<string>;
	hiddenLabel?: (count: number) => string;
	onSelect: (reference: string) => void;
	/** Shows an edit button on hovered and selected rows */
	onEdit?: (token: CompiledDesignToken) => void;
};

export function TokenPreview({
	token,
	value = token.value.base,
}: {
	token: CompiledDesignToken;
	value?: string | number;
}) {
	return (
		<span className={css.preview}>
			{token.type === 'color' ? (
				<span className={css.swatch} aria-hidden="true">
					<span style={{ backgroundColor: String(value) }} />
				</span>
			) : (
				<span className={css.glyph} aria-hidden="true">
					{{ dimension: '↔', fontFamily: 'Aa', number: '#' }[token.type]}
				</span>
			)}
			<span className={css.details}>
				<span className={css.name}>{token.name}</span>
				<span className={css.value}>{String(value)}</span>
			</span>
		</span>
	);
}

/** Shared token choices for the Inspector and resource editors. Hosts own import/apply behavior. */
export default function DesignTokenPicker({
	sources,
	search,
	onSearchChange,
	onClose,
	modeSelection = {},
	busy,
	dark,
	dashboard,
	footer,
}: {
	sources: TokenSource[];
	search: string;
	onSearchChange: (value: string) => void;
	onClose: () => void;
	modeSelection?: Record<string, string>;
	busy?: boolean;
	dark?: boolean;
	dashboard?: boolean;
	footer?: React.ReactNode;
}) {
	// Native autoFocus may scroll the host before the overlay has been positioned.
	const focusSearch = useCallback((input: InputRef | null) => input?.focus({ preventScroll: true }), []);
	const query = search.trim().toLowerCase();
	const sections = sources.map(source => {
		const groups = new Map<string, { name: string; tokens: CompiledDesignToken[] }>();
		let hiddenCount = 0;
		for (const token of source.tokens) {
			const value = resolveDesignModeValue<string | number>(token.value, modeSelection);
			if (
				query &&
				![token.name, value, token.collectionName, token.type].some(part =>
					String(part).toLowerCase().includes(query)
				)
			)
				continue;
			if (source.hiddenTokenIds?.has(token.id)) {
				hiddenCount += 1;
				continue;
			}
			const group = groups.get(token.collectionId) ?? { name: token.collectionName, tokens: [] };
			group.tokens.push(token);
			groups.set(token.collectionId, group);
		}
		return { source, groups: [...groups.entries()], hiddenCount };
	});
	return (
		<div
			className={css.panel}
			data-theme={dark ? 'dark' : 'light'}
			data-dashboard={dashboard || undefined}
			onKeyDown={event => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					onClose();
				}
			}}
		>
			<header className={css.header}>
				<strong>{t('designTokens.connect')}</strong>
				<Input
					ref={focusSearch}
					className={dashboard ? pickerSearchClassName : undefined}
					allowClear
					aria-label={t('designTokens.search')}
					placeholder={t('designTokens.search')}
					value={search}
					onChange={event => onSearchChange(event.target.value)}
				/>
			</header>
			<div className={css.list}>
				{sections.map(({ source, groups, hiddenCount }) =>
					groups.length ? (
						<section key={source.id} className={css.source}>
							{source.label ? (
								<h3>
									{source.label}
									{hiddenCount > 0 && source.hiddenLabel ? (
										<span>{source.hiddenLabel(hiddenCount)}</span>
									) : null}
								</h3>
							) : null}
							{groups.map(([id, group]) => (
								<div className={css.group} key={id}>
									<h4>{group.name}</h4>
									{group.tokens.map(token => {
										const selected = token.id === source.selectedTokenId;
										return (
											<div className={css.rowItem} key={token.id} data-selected={selected}>
												<button
													type="button"
													className={css.row}
													aria-pressed={selected}
													disabled={busy}
													onClick={() => source.onSelect(token.reference)}
												>
													<TokenPreview
														token={token}
														value={resolveDesignModeValue<string | number>(
															token.value,
															modeSelection
														)}
													/>
													{selected ? (
														<span className={css.check} aria-hidden="true">
															✓
														</span>
													) : null}
												</button>
												{source.onEdit ? (
													<button
														type="button"
														className={css.rowEdit}
														aria-label={`${t('designTokens.editValue')}: ${token.name}`}
														title={t('designTokens.editValue')}
														disabled={busy}
														onClick={() => source.onEdit?.(token)}
													>
														<EditOutlined />
													</button>
												) : null}
											</div>
										);
									})}
								</div>
							))}
						</section>
					) : null
				)}
				{sections.every(section => !section.groups.length) ? (
					<div className={css.empty}>{t('designTokens.noMatches')}</div>
				) : null}
			</div>
			{footer || dashboard ? (
				<footer className={css.footer}>
					{footer}
					{dashboard ? <span className={css.hint}>{t('designTokens.escToClose')}</span> : null}
				</footer>
			) : null}
		</div>
	);
}
